/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { callAllHandlers, useIsComposingHandler, useLiveRef } from "@smartsheet/lodestar-utils";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import React, { useState, useEffect, forwardRef } from "react";
import { Omit } from "utility-types";
import { noop } from "../../constants/noop";
import { useTheme } from "../../theme/utils/useTheme";
import { useFocusMode } from "../focusMode";
import { formInputTheme } from "../textinput/styled/formInputTheme";
import { VERTICAL_PADDING, LINE_HEIGHT_PX, SIZING_BUFFER, TextAreaStyled } from "./styled/TextAreaStyled";

const DEFAULT_MIN_LINES = 4;

export interface TextAreaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "disabled" | "onChange" | "value"> {
    /** Handler to be called when the input changes. */
    onChange: (value: string, event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    /** The value of the textarea. */
    value: string;
    /**
     * A `clientId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests */
    clientId?: string;
    /**
     * Don't allow users to manually resize the textarea. This can be advantageous if you want the
     * textarea to always grow along with its content.
     */
    disableManualResize?: boolean;
    /** Flag to determine if the input is disabled */
    isDisabled?: boolean;
    /** The max height of the textarea, e.g. "400px" */
    maxHeight?: string;
    /**
     * The min height of the textarea, e.g. "48px". Text areas are always at least one line tall,
     * so you can use "0" to get that minimal one line height. */
    minHeight?: string;
    /** If "error" then it gets a red border. */
    validationState?: "error";
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((props, parentRef) => {
    const {
        // We need an e2e test for this prop.
        // See https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/326
        disableManualResize = false,
        value,
        onChange,
        minHeight,
        maxHeight,
        clientId,
        isDisabled = false,
        style: propStyle,
        validationState,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyUp,
        onMouseEnter,
        onMouseLeave,
        ...htmlAttributes
    } = props;

    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { focusMode } = useFocusMode();
    const borderWidth = formInputTheme.borderWidth.rest(themeMode);
    const defaultHeight = borderWidth * 2 + VERTICAL_PADDING * 2 + DEFAULT_MIN_LINES * Math.floor(LINE_HEIGHT_PX);

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const textAreaRef = useEnsuredForwardedRef(parentRef);

    // The inline value for CSS height. Values transitions might look like:
    // 0 => 60px => 0 => 60px => 0 => 80px ...
    // The momentary "0" value rubber bands the height to the content
    const [height, setHeight] = useState<string>("0");

    // The height as reported by ResizeObserver (available in all browsers
    // except IE). This value changes for 3 reasons:
    // 1. Initial state is reported as soon as you register an observer
    // 2. Auto-grow/shrink based on more or less content
    // 3. Manually dragging the resize handle
    const [observedHeight, setObservedHeight] = useState<number>(0);

    // The height that the user manually dragged the vertical resize handle
    // to.
    const [manualHeight, setManualHeight] = useState<number>(0);

    // The most recently calculated content height, as generated by momentarily
    // setting the height to auto.
    const [contentHeight, setContentHeight] = useState<number>(0);

    function handleOnChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        if (onChange) {
            onChange(e.target.value, e);
        }
    }

    const handleKeyDown = useIsComposingHandler(onKeyDown || noop);
    const handleKeyUp = useIsComposingHandler(onKeyUp || noop);

    // Place this effect first so the initial run doesn't reset the height
    useEffect(() => {
        // This momentarily removes the explicit numeric height, so that
        // the native content height can be interrogated.
        setHeight("0");
    }, [value]);

    useEffect(() => {
        if (textAreaRef?.current !== null) {
            const newHeight = textAreaRef.current.scrollHeight + SIZING_BUFFER + borderWidth * 2;

            // Use an additional small number for the comparison here to avoid rounding errors from zoom.
            // Without any extra padding, even a tiny floating point or zoom error could result in an infinite loop.
            const CHANGE_SIZE_MINIMUM_DELTA = 2;
            const hasHeightChangedEnough =
                Math.abs(newHeight - contentHeight) > SIZING_BUFFER + CHANGE_SIZE_MINIMUM_DELTA;

            if (hasHeightChangedEnough || height === "0") {
                setHeight(`${manualHeight ? manualHeight : newHeight}px`);
            }

            if (hasHeightChangedEnough) {
                setContentHeight(newHeight);
            }
        }
    }, [contentHeight, height, manualHeight, borderWidth, textAreaRef, textAreaRef.current?.scrollHeight]);

    useEffect(() => {
        if (disableManualResize || !window.ResizeObserver) {
            return;
        }

        const observer = new ResizeObserver(() => {
            if (textAreaRef.current) {
                const height = textAreaRef.current.getBoundingClientRect().height;
                setObservedHeight(height);
            }
        });

        if (textAreaRef.current) {
            observer.observe(textAreaRef.current);
        }

        return () => observer.disconnect();
    }, [textAreaRef, height, disableManualResize]);

    // Need this to only run when observedHeight changes, so not
    // including contentHeight despite the lint warning. If we included
    // contentHeight as well, the box erroroneously updates
    // manualHeight when no drag handle operation occurred.
    const latestContentHeightRef = useLiveRef(contentHeight);
    useEffect(() => {
        if (disableManualResize) {
            return;
        }
        // ResizeObserver will notify regardless of if the change was programmatic
        // (smart resize) or manual (drag handle). To disambiguate we can use a bit
        // of lookbehind to compare the last calculated content height with the
        // currently reported ResizeObserver height. This avoids ugly hacks like
        // timers.
        if (Math.abs(observedHeight - latestContentHeightRef.current) > SIZING_BUFFER) {
            setManualHeight(observedHeight);
        }
    }, [observedHeight, disableManualResize, latestContentHeightRef]);

    // Disable drag handle in IE
    const resizeStyleOverride: React.CSSProperties = {
        resize: window.ResizeObserver ? undefined : "none",
    };

    return (
        <TextAreaStyled
            isDisabled={isDisabled}
            isFocused={isFocused}
            isHovered={isHovered}
            disableManualResize={disableManualResize}
            value={value}
            focusMode={focusMode}
            onChange={handleOnChange}
            onFocus={callAllHandlers(() => setIsFocused(true), onFocus)}
            onBlur={callAllHandlers(() => setIsFocused(false), onBlur)}
            onKeyDown={(onKeyDown && handleKeyDown) || undefined}
            onKeyUp={(onKeyUp && handleKeyUp) || undefined}
            onMouseEnter={callAllHandlers(() => setIsHovered(true), onMouseEnter)}
            onMouseLeave={callAllHandlers(() => setIsHovered(false), onMouseLeave)}
            ref={textAreaRef}
            validationState={validationState}
            style={{
                ...propStyle,
                height,
                maxHeight: maxHeight,
                minHeight: minHeight || defaultHeight,
                ...resizeStyleOverride,
            }}
            data-client-id={clientId}
            themeMode={themeMode}
            aria-disabled={isDisabled}
            {...htmlAttributes}
            readOnly={isDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false}
        />
    );
});
